// TERM3 (docs/build-briefs/TERM3-sign-in-remembers-last-terminal.md): what the
// app does about the payment terminal when someone signs in. Pure so each branch
// is proven by a unit test.

/** What GET .../me/terminal returned: the user's last terminal, only when the
 * server found it still active and chargeable, otherwise null. The server never
 * returns a deactivated terminal, so this model never has to decide to skip one. */
export interface RememberedTerminalDto {
  id: string;
  display_name: string;
}

export type SignInTerminalDecision =
  /** Select the remembered terminal on this device. */
  | { kind: "use_remembered"; id: string; displayName: string }
  /** The device already has the remembered terminal; change nothing. */
  | { kind: "already_selected" }
  /** Nothing remembered (or not readable) but the device has a choice; keep it. */
  | { kind: "keep_device" }
  /** Neither: the app shows "Card reader not set up" and asks as it always has. */
  | { kind: "ask" }
  /** TERM4: the device holds a reader that is not one of this bar's readers (a tablet moved to another bar, or a
   * reader removed from this bar). Forget it and show the picker. */
  | { kind: "drop_device_and_ask" }
  /** PAY1-S4 (walkthrough item 14): the device has no usable reader and the bar has exactly one. Use it, so a reader
   * a manager registered in Settings does not wait for someone to pick it. */
  | { kind: "use_only_reader"; id: string; displayName: string };

/** PAY1-S4. Settings fires this on `window` after a card reader is registered, so a device with no reader can take
 * the location's only one (see attachOnlyReaderIfDeviceHasNone in main.ts). A plain event: admin.ts cannot import
 * main.ts. It carries nothing. */
export const CARD_READER_REGISTERED_EVENT = "bms:card-reader-registered";

/** One of the location's chargeable readers, as the selectable list returns it. */
export interface LocationReader {
  id: string;
  display_name: string;
}

/** PAY1-S4. The reader to attach on its own: only when the location lists EXACTLY one. Zero keeps the "not set up"
 * state; two or more keep the picker, because choosing between them is a person's decision. `undefined` (the list
 * could not be read in time) attaches nothing. */
export function soleReaderOf(
  readers: readonly LocationReader[] | undefined,
): LocationReader | undefined {
  return readers !== undefined && readers.length === 1 ? readers[0] : undefined;
}

export function decideSignInTerminal(params: {
  remembered: RememberedTerminalDto | null | undefined;
  deviceTerminalId: string | undefined;
  /** TERM4: whether the device's own saved reader is in this location's list of readers. `undefined` means the list
   * could not be read in time, and the device's choice then stands (Pay re-checks on the server either way). */
  deviceTerminalAtThisLocation?: boolean | undefined;
  /** PAY1-S4: the location's chargeable readers, when the list could be read in time. */
  readersAtThisLocation?: readonly LocationReader[] | undefined;
}): SignInTerminalDecision {
  const { remembered, deviceTerminalId } = params;
  const only = soleReaderOf(params.readersAtThisLocation);
  if (remembered !== null && remembered !== undefined) {
    if (remembered.id === deviceTerminalId) {
      return { kind: "already_selected" };
    }
    return { kind: "use_remembered", id: remembered.id, displayName: remembered.display_name };
  }
  if (deviceTerminalId === undefined) {
    return only === undefined
      ? { kind: "ask" }
      : { kind: "use_only_reader", id: only.id, displayName: only.display_name };
  }
  if (params.deviceTerminalAtThisLocation === false) {
    return only === undefined
      ? { kind: "drop_device_and_ask" }
      : { kind: "use_only_reader", id: only.id, displayName: only.display_name };
  }
  return { kind: "keep_device" };
}

export function signInTerminalToast(displayName: string): string {
  return `Card payments will use ${displayName}.`;
}

/** TERM4: shown when a saved reader from another location is forgotten at sign-in. */
export const OTHER_LOCATION_TERMINAL_DROPPED_TOAST =
  "The card reader saved on this device is not at this location, so it was removed.";

export const TERMINAL_NOT_SAVED_TOAST =
  "Card reader not saved for next sign-in. It still works on this device.";

/** How long sign-in waits for the remembered-terminal answer before carrying on
 * without it. Open Tabs is already showing; a late answer is ignored. */
export const REMEMBERED_TERMINAL_WAIT_MS = 1500;

/** The top bar's terminal label. */
export function topbarTerminalLabel(displayName: string): string {
  return `Card reader: ${displayName}`;
}
